import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KoobDataTable,
  KoobEmptyState,
  KoobTableAction,
  KoobTooltip,
} from '@koob/ui';
import {
  BookingStatusBadge,
  FolderBookingSourceBadge,
  FolderBookingTypeIcon,
  FolderClampedText,
  FolderBookingsFilters,
  FolderDateRange,
  FolderTravelDiaryAction,
} from '@koob/booking';
import {
  buildFolderBookingList,
  FOLDER_ROWS_ALL,
  FolderRowSortKeyEnum,
  FolderRowTypeEnum,
  FolderServiceModeEnum,
  sortFolderBookingList,
} from '@koob/utils';
import { AdminBookingStateEnum } from '@koob/enums';
import { formatDateTime } from '@koob/dates';
import { formatCurrency } from 'utils';
import ExperienceGuaranteedDepartureIcon from '../../../components/Experience/ExperienceGuaranteedDepartureIcon';
import FoldersBookingEditButton from './FoldersBookingEditButton';
import FoldersTripDraftEditButton from './FoldersTripDraftEditButton';

const ROW_TRIP = 'trip';
const ROW_SERVICE = 'service';
const ROW_SECTION = 'section';
// Last row of the table: rows shown and filtered total, which then sits
// right under the Amount column (the table footer spans every column).
const ROW_FOOTER = 'footer';

// KD-3903: one amount per currency, never converted (1 250,00 $ + 1 818 300 ₫).
// `currency` stands in for an amount whose currency is unknown.
const Amount = ({ amounts, currency, bold }) =>
  amounts.length === 0 ? (
    <span className="text-gray-400">--</span>
  ) : (
    <span className={bold ? 'font-bold' : 'font-semibold'}>
      {amounts
        .map(item =>
          formatCurrency({
            amount: item.amount,
            currency: item.currency ?? currency,
          }),
        )
        .join(' + ')}
    </span>
  );

// An empty list still totals 0, in the folder's currency.
const totalAmounts = (amounts, currency) =>
  amounts.length > 0 ? amounts : [{ currency, amount: 0 }];

/**
 * KD-3842 TO bookings list of a client folder: one list grouped by trip,
 * standalone services below. Data, filters and sort come from @koob/utils,
 * cells from @koob/booking; columns and actions are the TO's own.
 */
export default function FoldersBookingsTable({ folder, archived }) {
  const { t } = useTranslation('folders');
  const [type, setType] = useState(FOLDER_ROWS_ALL);
  const [status, setStatus] = useState(FOLDER_ROWS_ALL);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(null);
  const [openTrips, setOpenTrips] = useState({});

  const list = sortFolderBookingList(
    buildFolderBookingList(folder, { type, status, query }),
    sort,
  );
  const currency = folder?.currency;

  const toggleTrip = tripId =>
    setOpenTrips(current => ({ ...current, [tripId]: !current[tripId] }));

  const rows = [];
  list.trips.forEach(trip => {
    const open = trip.expandable && (openTrips[trip.id] || trip.forceExpanded);
    rows.push({ ...trip, rowKind: ROW_TRIP, open });
    if (open) {
      trip.items.forEach(service =>
        rows.push({ ...service, rowKind: ROW_SERVICE, child: true }),
      );
    }
  });
  if (list.standalone.length > 0) {
    rows.push({ rowKind: ROW_SECTION });
    list.standalone.forEach(service =>
      rows.push({ ...service, rowKind: ROW_SERVICE, child: false }),
    );
  }
  // Trips expanded by hand show their services too.
  const shownCount = rows.filter(row => row.rowKind !== ROW_SECTION).length;
  if (rows.length > 0) {
    rows.push({ rowKind: ROW_FOOTER });
  }

  const serviceDetail = service => {
    const parts = [
      service.mode === FolderServiceModeEnum.LIVE
        ? t('bookingsList.modeLive')
        : t('bookingsList.modeOnRequest'),
    ];
    if (service.type === FolderRowTypeEnum.HOTEL) {
      parts.push(
        service.roomCount > 1
          ? t('bookingsList.rooms', { count: service.roomCount })
          : service.roomName,
      );
      if (service.nights) {
        parts.push(t('bookingsList.nights', { count: service.nights }));
      }
    } else if (service.pax) {
      parts.push(t('bookingsList.pax', { count: service.pax }));
    }
    return parts.filter(Boolean).join(' · ');
  };

  const tripComposition = trip =>
    trip.draft
      ? t('bookingsList.draftServices')
      : `${t('bookingsList.hotels', { count: trip.hotelsCount })} · ${t(
          'bookingsList.experiences',
          { count: trip.experiencesCount },
        )}`;

  // Section and footer rows only fill the columns they need.
  const onlyFor = (row, render) =>
    row.rowKind === ROW_SECTION || row.rowKind === ROW_FOOTER ? null : render();

  const columns = [
    {
      key: FolderRowSortKeyEnum.ID,
      label: t('bookingsList.columns.id'),
      sortable: true,
      // KD-3910: services show their booking id too, not only trips.
      render: row => onlyFor(row, () => row.id),
    },
    {
      key: FolderRowSortKeyEnum.NAME,
      label: t('bookingsList.columns.name'),
      sortable: true,
      render: row => {
        if (row.rowKind === ROW_FOOTER) {
          return (
            <span className="whitespace-nowrap text-xs text-gray-500">
              {t('bookingsList.rowsShown', {
                shown: shownCount,
                total: list.counts[FOLDER_ROWS_ALL],
              })}
            </span>
          );
        }
        if (row.rowKind === ROW_TRIP) {
          return (
            <span className="flex items-center gap-2">
              <i
                className={`fa-regular fa-chevron-right text-xs text-gray-500 transition-transform ${
                  row.open ? 'rotate-90' : ''
                } ${row.expandable ? '' : 'invisible'}`}
                aria-hidden="true"
              />
              <span className="flex min-w-[8rem] flex-col">
                <FolderClampedText text={row.name} className="font-bold" />
                <FolderClampedText
                  text={tripComposition(row)}
                  lines={1}
                  className="text-xs text-gray-500"
                />
              </span>
            </span>
          );
        }
        return (
          <span
            className={`flex min-w-[8rem] flex-col ${row.child ? 'pl-6' : ''}`}
          >
            <FolderClampedText text={row.name} className="font-medium" />
            <FolderClampedText
              text={serviceDetail(row)}
              lines={1}
              className="text-xs text-gray-500"
            />
          </span>
        );
      },
    },
    {
      key: FolderRowSortKeyEnum.TYPE,
      label: t('bookingsList.columns.type'),
      sortable: true,
      render: row =>
        onlyFor(row, () => (
          <FolderBookingTypeIcon
            type={row.type}
            experienceType={row.experienceType}
          />
        )),
    },
    {
      key: FolderRowSortKeyEnum.CITY,
      label: t('bookingsList.columns.city'),
      sortable: true,
      render: row =>
        onlyFor(row, () => (
          <FolderClampedText
            text={
              row.rowKind === ROW_TRIP
                ? [row.startLocation, row.endLocation]
                    .filter(Boolean)
                    .join(' → ')
                : row.city
            }
          />
        )),
    },
    {
      key: FolderRowSortKeyEnum.TIR,
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t('bookingsList.columns.tir')}
          <KoobTooltip tip={t('bookings.internalToReference')}>
            <i className="far fa-info-circle" />
          </KoobTooltip>
        </span>
      ),
      sortable: true,
      render: row =>
        onlyFor(row, () => <FolderClampedText text={row.toReference} />),
    },
    {
      key: FolderRowSortKeyEnum.BOOKED_ON,
      label: t('bookingsList.columns.bookedOn'),
      sortable: true,
      render: row =>
        onlyFor(row, () =>
          row.bookedAt ? formatDateTime(row.bookedAt, 'DD/MM/YYYY') : null,
        ),
    },
    {
      key: FolderRowSortKeyEnum.DATES,
      label: t('bookingsList.columns.dates'),
      sortable: true,
      render: row =>
        onlyFor(row, () => (
          <FolderDateRange startAt={row.startAt} endAt={row.endAt} />
        )),
    },
    {
      key: FolderRowSortKeyEnum.PROVIDER,
      label: t('bookingsList.columns.provider'),
      sortable: true,
      render: row =>
        onlyFor(row, () => <FolderClampedText text={row.provider} />),
    },
    {
      key: FolderRowSortKeyEnum.SOURCE,
      label: t('bookingsList.columns.source'),
      sortable: true,
      render: row =>
        row.rowKind === ROW_SERVICE ? (
          <FolderBookingSourceBadge source={row.source} />
        ) : null,
    },
    {
      key: FolderRowSortKeyEnum.STATUS,
      label: t('bookingsList.columns.status'),
      sortable: true,
      render: row =>
        onlyFor(row, () => <BookingStatusBadge status={row.status} />),
    },
    {
      key: FolderRowSortKeyEnum.GD,
      label: t('bookingsList.columns.gd'),
      sortable: true,
      render: row => {
        if (row.rowKind === ROW_FOOTER) {
          return (
            <span className="block text-right text-xs text-gray-500">
              {t('bookingsList.total')}
            </span>
          );
        }
        return row.rowKind === ROW_SERVICE &&
          row.type === FolderRowTypeEnum.EXPERIENCE &&
          row.guaranteedDeparture ? (
          <ExperienceGuaranteedDepartureIcon />
        ) : null;
      },
    },
    {
      key: FolderRowSortKeyEnum.AMOUNT,
      label: t('bookingsList.columns.amount'),
      align: 'right',
      sortable: true,
      render: row =>
        row.rowKind === ROW_FOOTER ? (
          <Amount
            amounts={totalAmounts(list.filteredTotals, currency)}
            currency={currency}
            bold
          />
        ) : (
          onlyFor(row, () => (
            <Amount
              amounts={row.amounts}
              currency={currency}
              bold={row.rowKind === ROW_TRIP}
            />
          ))
        ),
    },
  ];

  const actions = row => {
    if (row.rowKind === ROW_SECTION || row.rowKind === ROW_FOOTER) return null;
    if (row.rowKind === ROW_TRIP) {
      return (
        <div className="flex items-center justify-end gap-1">
          {row.isProposal ? (
            <KoobTooltip tip={t('bookings.proposalDisabled')} position="left">
              <i className="fa-regular fa-dollar-sign text-lg" />
            </KoobTooltip>
          ) : (
            <KoobTableAction
              icon={<i className="fa-regular fa-dollar-sign" />}
              label={t('bookings.priceSummary')}
              to={`/trips/${row.id}/receipt`}
              onClick={() => window.scrollTo(0, 0)}
            />
          )}
          {row.draft && !archived && (
            <FoldersTripDraftEditButton tripId={row.id} />
          )}
          {!row.draft && (
            <KoobTableAction
              icon={<i className="fa-regular fa-messages" />}
              label={t('tripDesigner:conversation.chat.title')}
              to={`/trips/${row.id}/chat`}
              onClick={() => window.scrollTo(0, 0)}
            />
          )}
          <FolderTravelDiaryAction
            href={`/trips/${row.id}/export`}
            viewCount={row.trip?.diaryViewCount}
            firstViewedAt={row.trip?.diaryFirstViewedAt}
            lastViewedAt={row.trip?.diaryLastViewedAt}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-end gap-1">
        {row.status !== AdminBookingStateEnum.REPLACED && (
          <KoobTableAction
            icon={<i className="fa-regular fa-dollar-sign" />}
            label={t('bookings.priceSummary')}
            to={`/bookings/${row.id}/receipt`}
            onClick={() => window.scrollTo(0, 0)}
          />
        )}
        <FoldersBookingEditButton
          bookingId={row.id}
          testId={`folder-booking-${row.id}`}
        />
      </div>
    );
  };

  const noService = list.counts[FOLDER_ROWS_ALL] === 0;

  return (
    <KoobDataTable
      headerBar={
        <div className="flex flex-wrap items-center gap-3 border-0 border-b border-solid border-border px-4 py-3">
          <span className="text-sm font-bold text-gray-900">
            {t('bookings.title')}
          </span>
          <FolderBookingsFilters
            type={type}
            onTypeChange={setType}
            counts={list.counts}
            query={query}
            onQueryChange={setQuery}
            status={status}
            onStatusChange={setStatus}
          />
        </div>
      }
      columns={columns}
      data={rows}
      sort={sort}
      onSortChange={setSort}
      actions={actions}
      onRowClick={row =>
        row.rowKind === ROW_TRIP && row.expandable && toggleTrip(row.id)
      }
      fullWidthRow={row =>
        row.rowKind === ROW_SECTION ? (
          <span className="flex items-baseline gap-2.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              {t('bookingsList.standalone')}
            </span>
            <span className="text-xs text-gray-400">
              {t('bookingsList.standaloneHint')}
            </span>
          </span>
        ) : null
      }
      rowProps={row => ({
        style: {
          cursor:
            row.rowKind === ROW_TRIP && row.expandable ? 'pointer' : 'default',
          backgroundColor:
            row.rowKind === ROW_SECTION ||
            row.rowKind === ROW_FOOTER ||
            row.child
              ? '#fafafa'
              : undefined,
        },
        'data-testid': `folder-bookings-row-${row.rowKind}`,
      })}
      emptyState={
        <KoobEmptyState
          variant="plain"
          icon={<i className="fa-regular fa-folder-open" aria-hidden="true" />}
          title={
            noService
              ? t('bookingsList.emptyTitle')
              : t('bookingsList.noMatchTitle')
          }
          description={
            noService
              ? t('bookingsList.emptyDescription')
              : t('bookingsList.noMatchDescription')
          }
        />
      }
    />
  );
}
